import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import Grid from '@material-ui/core/Grid';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import { Checkbox, IconButton } from '@material-ui/core';
import { useTable, useSortBy, useRowSelect } from 'react-table';
import { Box } from '@material-ui/core';
import DeleteOutlineRounded from '@material-ui/icons/DeleteOutlineRounded';
import EditIcon from '@material-ui/icons/Edit';
import Pagination from '@material-ui/lab/Pagination';

const BuzzTableCheckbox = React.forwardRef(
  ({ indeterminate, ...rest }, ref) => {
    const defaultRef = React.useRef();
    const resolvedRef = ref || defaultRef;

    React.useEffect(() => {
      resolvedRef.current.indeterminate = indeterminate;
    }, [resolvedRef, indeterminate]);

    return <Checkbox type="checkbox" ref={resolvedRef} {...rest} />;
  }
);

const BuzzTable = forwardRef(
  (
    {
      columns,
      data,
      paper = false,
      selectKey = '',
      onSelect = (a) => {},
      deleteRow = null,
      updateRow = null,
      isHandlePagination = false,
    },
    ref
  ) => {
    const [page, setPage] = useState(1);

    const tableColumns = useMemo(() => columns, [columns]);
    const tableData = useMemo(() => data || [], [data]);

    const tableInstance = useTable(
      {
        columns: tableColumns,
        data: tableData,
      },
      useSortBy,
      useRowSelect,
      (hooks) => {
        if (selectKey && typeof onSelect === 'function') {
          hooks.visibleColumns.push((columns) => [
            {
              id: 'selection',
              Header: ({ getToggleAllRowsSelectedProps }) => (
                <BuzzTableCheckbox {...getToggleAllRowsSelectedProps()} />
              ),
              Cell: ({ row }) => (
                <BuzzTableCheckbox {...row.getToggleRowSelectedProps()} />
              ),
            },
            ...columns,
          ]);
        } else {
          hooks.visibleColumns.push((columns) => [...columns]);
        }
      }
    );

    const {
      getTableProps,
      getTableBodyProps,
      headerGroups,
      rows,
      prepareRow,
      selectedFlatRows,
    } = tableInstance;

    const [rowsToShow, setRowsToShow] = useState([]);
    useEffect(() => {
      if (isHandlePagination)
        setRowsToShow(rows.slice((page - 1) * 8, (page - 1) * 8 + 8));
      else setRowsToShow(rows);
    }, [rows, page]);

    useEffect(() => {
      if (selectKey && typeof onSelect === 'function') {
        if (selectKey !== 'all')
          onSelect(
            selectedFlatRows.map((selected) => selected.values[selectKey])
          );
        else onSelect(selectedFlatRows.map((selected) => selected.values));
      }
    }, [selectedFlatRows.length]);

    const handleChangePage = (event, newPage) => {
      setPage(newPage);
    };

    useImperativeHandle(ref, () => ({
      resetPageIdx: () => handleChangePage(null, 1),
    }));

    return (
      <TableContainer component={paper ? Paper : Box}>
        <Table {...getTableProps()}>
          <TableHead>
            {headerGroups.map((headerGroup, id) => (
              <TableRow {...headerGroup.getHeaderGroupProps()} key={id}>
                {headerGroup.headers.map((column, idx) => (
                  <TableCell
                    key={idx}
                    {...column.getHeaderProps(column.getSortByToggleProps())}>
                    {column.render('Header')}
                    <span>
                      {column.isSorted
                        ? column.isSortedDesc
                          ? '▼'
                          : '▲'
                        : null}
                    </span>
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody {...getTableBodyProps()}>
            {rowsToShow.map((row, idx) => {
              prepareRow(row);
              return (
                <TableRow key={idx} {...row.getRowProps()}>
                  {row.cells.map((cell, id) => {
                    return (
                      <TableCell key={id} {...cell.getCellProps()}>
                        {cell.render('Cell')}
                      </TableCell>
                    );
                  })}
                  {updateRow && (
                    <TableCell>
                      <IconButton onClick={() => updateRow(row)}>
                        <EditIcon color="secondary" />
                      </IconButton>
                    </TableCell>
                  )}
                  {deleteRow && (
                    <TableCell>
                      <IconButton onClick={() => deleteRow(row)}>
                        <DeleteOutlineRounded color="primary" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {isHandlePagination && (
          <Grid
            container
            justify="center"
            style={{ marginTop: '42px', marginBottom: '42px' }}>
            <Pagination
              showFirstButton
              showLastButton
              variant="outlined"
              shape="rounded"
              count={
                rows.length / 8 === Math.floor(rows.length / 8)
                  ? rows.length / 8
                  : Math.floor(rows.length / 8) + 1
              }
              page={page}
              onChange={handleChangePage}
            />
          </Grid>
        )}
      </TableContainer>
    );
  }
);

export default BuzzTable;
